import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Building2, Mail, MapPin, Loader2, Save, AlertTriangle, Fingerprint } from 'lucide-react';
import { toast } from 'sonner';
import { isNative } from '../../lib/native/platform';
import { isBiometricAvailable, verifyBiometric } from '../../lib/native/biometric';
import { prefGet, prefSet } from '../../lib/native/preferences';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    contactInfo: { phone: '', address: '' },
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/admin/profile`);
      setProfile(response.data);
      setFormData({
        companyName: response.data.company.companyName,
        contactInfo: response.data.company.contactInfo || { phone: '', address: '' },
      });
    } catch (error) {
      toast.error('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/admin/profile`, formData);
      toast.success('Profile updated successfully');
      fetchProfile();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Company Profile</h1>
          <p className="text-slate-600 mt-1">Manage your company information</p>
        </div>

        {/* User Info Card */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">User Information</h2>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-600">Email</p>
                <p className="font-medium text-slate-900">{profile?.user.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-600">Name</p>
                <p className="font-medium text-slate-900">{profile?.user.name}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Company Info Form */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Company Information</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Company Name</label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                required
                data-testid="company-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
              <input
                type="tel"
                value={formData.contactInfo.phone}
                onChange={(e) => setFormData({ ...formData, contactInfo: { ...formData.contactInfo, phone: e.target.value } })}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                data-testid="phone-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Address</label>
              <textarea
                value={formData.contactInfo.address}
                onChange={(e) => setFormData({ ...formData, contactInfo: { ...formData.contactInfo, address: e.target.value } })}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                rows={3}
                data-testid="address-input"
              />
            </div>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 shadow-md"
              data-testid="save-profile-button"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Email Sender Card */}
        <EmailSenderCard />

        {/* Deviation Tolerance Card */}
        <DeviationSettingsCard />

        {/* Biometric unlock (native app only) */}
        <BiometricSettingsCard />
      </div>
    </Layout>
  );
};

const BiometricSettingsCard = () => {
  const [available, setAvailable] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!isNative()) return;
      const avail = await isBiometricAvailable();
      setAvailable(avail);
      if (avail) setEnabled((await prefGet('rvx_biometric_enabled')) === 'true');
    })();
  }, []);

  // Native-only feature — invisible in an ordinary browser.
  if (!isNative() || !available) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      if (!enabled) {
        const ok = await verifyBiometric('Enable biometric unlock');
        if (!ok) { toast.error('Could not verify identity'); return; }
        await prefSet('rvx_biometric_enabled', 'true');
        setEnabled(true);
        toast.success('Biometric unlock enabled');
      } else {
        await prefSet('rvx_biometric_enabled', 'false');
        setEnabled(false);
        toast.success('Biometric unlock disabled');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-testid="biometric-settings-card">
      <div className="flex items-center gap-2 mb-1">
        <Fingerprint className="w-5 h-5 text-blue-500" />
        <h2 className="text-lg font-semibold text-slate-900">Biometric App Unlock</h2>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Require Face ID / fingerprint to open the app on this device. This is an extra lock on top of
        your normal sign-in — it never replaces your account login.
      </p>
      <button
        onClick={toggle}
        disabled={busy}
        className={`flex items-center gap-2 px-6 py-2 rounded-lg text-white disabled:opacity-50 ${enabled ? 'bg-slate-600 hover:bg-slate-700' : 'bg-blue-600 hover:bg-blue-700'}`}
        data-testid="biometric-toggle-btn"
      >
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Fingerprint className="w-5 h-5" />}
        {enabled ? 'Disable' : 'Enable'} biometric unlock
      </button>
    </div>
  );
};

const DeviationSettingsCard = () => {
  const [tolerance, setTolerance] = useState(5);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/company/deviation-settings`).then((r) => setTolerance(r.data.tolerance ?? 5)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/company/deviation-settings`, { tolerance: parseFloat(tolerance) });
      toast.success('Deviation tolerance saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save tolerance');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-testid="deviation-settings-card">
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="w-5 h-5 text-red-500" />
        <h2 className="text-lg font-semibold text-slate-900">Deviation Tolerance</h2>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Object counts and measurements are flagged when they differ from the planned/expected value by more than this
        percentage. Default is 5%.
      </p>
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      ) : (
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="relative">
            <input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={tolerance}
              onChange={(e) => setTolerance(e.target.value)}
              className="w-32 px-4 py-2 pr-8 bg-slate-50 border border-slate-300 rounded-lg outline-none"
              data-testid="tolerance-input"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
          </div>
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50" data-testid="tolerance-save-btn">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save
          </button>
        </div>
      )}
    </div>
  );
};

const EmailSenderCard = () => {
  const [emailFrom, setEmailFrom] = useState('');
  const [platformConfigured, setPlatformConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/company/email-settings`).then((r) => {
      setEmailFrom(r.data.emailFrom || '');
      setPlatformConfigured(!!r.data.platformConfigured);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/company/email-settings`, { emailFrom: emailFrom.trim() });
      toast.success('Email sender saved');
    } catch (e) {
      toast.error('Failed to save email sender');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-testid="tenant-email-card">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="w-5 h-5 text-amber-500" />
        <h2 className="text-lg font-semibold text-slate-900">Notification Email Sender</h2>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        The verified "from" address used on your team's punch-list emails (assignments & comments).
        {platformConfigured ? '' : ' Email delivery is disabled until the platform provider is configured.'}
      </p>
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      ) : (
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={emailFrom}
            onChange={(e) => setEmailFrom(e.target.value)}
            placeholder="notifications@yourcompany.com"
            className="flex-1 px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg outline-none"
            data-testid="tenant-email-from-input"
          />
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50" data-testid="tenant-email-save-btn">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save
          </button>
        </div>
      )}
    </div>
  );
};

export default Profile;
